/**
 * Centralised browser-state wipe for any auth-context change.
 *
 * Called by:
 *   - auth.store.ts → login()  (pre-clean before new identity loads)
 *   - auth.store.ts → logout() (clear before signOut to avoid races)
 *   - auth.store.ts → switchActiveContext() (org/tenant swap)
 *
 * Why centralised: leaving any localStorage key org-scoped but unwiped on
 * context change leaks the previous user's data into the next session
 * (the lwblueteddybear / Lisa bug — phantom experiences from a previous
 * tester bleeding into a fresh org via the global irlExperienceLibrary key).
 *
 * Add new keys here as they appear. Anything that should NOT survive an
 * identity or org/tenant swap must be wiped here.
 */
const CONTEXT_SCOPED_LOCAL_STORAGE_KEYS = [
  'irlExperienceLibrary',
] as const

const CONTEXT_SCOPED_LOCAL_STORAGE_KEY_PREFIXES = [
  'irlExperienceLibrary:',
  'irlCampaignDraft:',
  'irlAssetSelection:',
] as const

const canUseStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

export function clearContextCache(): void {
  if (!canUseStorage()) return

  for (const key of CONTEXT_SCOPED_LOCAL_STORAGE_KEYS) {
    window.localStorage.removeItem(key)
  }

  const allKeys: string[] = []
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i)
    if (key) allKeys.push(key)
  }

  for (const key of allKeys) {
    if (CONTEXT_SCOPED_LOCAL_STORAGE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      window.localStorage.removeItem(key)
    }
  }

  if (typeof window.sessionStorage !== 'undefined') {
    try {
      window.sessionStorage.clear()
    } catch {
      // ignore
    }
  }
}
